import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const API_BASE_URL = `${BASE_URL}/api/auth`

// Helper functions for token storage and retrieval
const getLocalRefreshToken = () => {
    return localStorage.getItem('refresh_token');
};

const getLocalAccessToken = () => {
    return localStorage.getItem('access_token');
};

const updateLocalAccessToken = (token) => {
    localStorage.setItem('access_token', token);
};

const updateLocalRefreshToken = (token) => {
    localStorage.setItem('refresh_token', token);
};

const removeLocalAccessToken = () => {
    localStorage.removeItem('access_token');
}

const removeLocalRefreshToken = () => {
    localStorage.removeItem('refresh_token');
}

// Axios instance with interceptors
const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

axiosInstance.interceptors.request.use(
    async (config) => {
        const token = getLocalAccessToken();
        if (token) {
            const decodedToken = jwtDecode(token);
            const isExpired = decodedToken.exp * 1000 < Date.now(); // Check if expired

            if (!isExpired) {
                config.headers.Authorization = `Bearer ${token}`;
                return config;
            }
        }

        // Token is expired or not available, try to refresh
        const refreshToken = getLocalRefreshToken();
        if (refreshToken) {
            try {
                const refreshResponse = await axios.post(
                    `${API_BASE_URL}/refresh/`,
                    { refresh: refreshToken }
                );

                const { access } = refreshResponse.data;
                updateLocalAccessToken(access);
                config.headers.Authorization = `Bearer ${access}`;
                return config;

            } catch (_error) {
                // Refresh failed, token is invalid, or refresh token expired
                removeLocalAccessToken();
                removeLocalRefreshToken();
                window.location.href = '/login'; // Redirect to login (or handle as needed)
                return Promise.reject(_error);
            }
        } else {
            // No refresh token available
            removeLocalAccessToken();
            removeLocalRefreshToken();
            window.location.href = '/login';
            return Promise.reject({
                response: { status: 401, data: 'No refresh token available' },
            });
        }
    },
    (error) => {
        return Promise.reject(error);
    }
);

// --- Response Interceptor ---
axiosInstance.interceptors.response.use(
  (response) => response, // If the response is successful, just return it.
  async (error) => {
    const originalRequest = error.config;

    // Check if the error is 401 Unauthorized and it's not a retry attempt already
    if (error.response?.status === 401 && !originalRequest._retry && originalRequest.url !== `${API_BASE_URL}/token/refresh/`) {
      originalRequest._retry = true; // Mark this request as being retried.

      try {
        const newAccessToken = getLocalRefreshToken(); // Call the refreshToken function
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        // Re-send the original request with the updated header.
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed. Logging out...", refreshError);
        logout(); // This will clear tokens and redirect to login page.
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;


export const signup = async (userData) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/signup/`, userData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const login = async (credentials) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/login/`, {
            username: credentials.identity,
            password: credentials.password,
        });

        const { refresh, access } = response.data;
        updateLocalAccessToken(access);
        updateLocalRefreshToken(refresh);

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const logout = async () => {
    try {
        const response = await axiosInstance.post(`${API_BASE_URL}/logout/`, {
            refresh: getLocalRefreshToken(),
        });
        removeLocalAccessToken();
        removeLocalRefreshToken();
        return response.data;
    } catch (error) {
        throw error;
    }
};


export const getUserProfile = async () => {
    try {
        const response = await axiosInstance.get(`${API_BASE_URL}/profile/`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const updateUserProfile = async (userData) => {
    try {
        const response = await axiosInstance.put(`${API_BASE_URL}/profile/`, userData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const updateUserPassword = async (newPassword) => {
    try {
        const response = await axiosInstance.post(
            `${API_BASE_URL}/profile/change-password/`,
            { new_password: newPassword }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const deleteUserAccount = async () => {
    try {
        const response = await axiosInstance.delete(`${API_BASE_URL}/profile/`);
        return response.data;
    } catch (error) {
        throw error;
    }
};